import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import { Wallet, Save, AlertCircle, Loader2 } from "lucide-react";
import { t } from "@/i18n";

const CHANNELS = [
  { key: "google", label: "Google Ads" },
  { key: "meta", label: "Meta (Facebook/Instagram)" },
  { key: "tiktok", label: "TikTok" },
  { key: "linkedin", label: "LinkedIn" },
];

const fmtEUR = (n) => n == null ? "—" :
  `€ ${Number(n).toLocaleString("nl-NL", { maximumFractionDigits: 2 })}`;

export default function WalletPage() {
  const { hideInnerHeader } = useEmbed();
  const [state, setState] = useState(null);
  const [cap, setCap] = useState("");
  const [mode, setMode] = useState("FIXED");
  const [caps, setCaps] = useState({ google: "", meta: "", tiktok: "", linkedin: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/wallet");
        setState(data);
        setCap(data.monthly_cap_eur != null ? String(data.monthly_cap_eur) : "");
        setMode(data.allocation_mode || "FIXED");
        setCaps({
          google: String(data.channel_caps?.google || 0),
          meta: String(data.channel_caps?.meta || 0),
          tiktok: String(data.channel_caps?.tiktok || 0),
          linkedin: String(data.channel_caps?.linkedin || 0),
        });
      } catch (e) {
        setError(e?.response?.data?.detail || t("common.error_generic"));
      }
    })();
  }, []);

  const capNum = Number(cap) || 0;
  const sumCaps = useMemo(() =>
    Object.values(caps).reduce((a, b) => a + (Number(b) || 0), 0), [caps]);
  const remaining = capNum - sumCaps;
  // M124-e: individueel kanaalbedrag mag niet negatief zijn. Server-side
  // blokkeert dit ook (Pydantic `_caps` validator → 422), maar we tonen
  // hier direct feedback zodat native browser-validatie ("please fill in")
  // niet als enige zichtbaar bericht overblijft.
  const negativeChannel = Object.entries(caps).find(
    ([, v]) => Number(v) < 0);
  const anyNegative = !!negativeChannel;
  const fixedInvalid = mode === "FIXED" && Math.abs(sumCaps - capNum) > 0.01 && capNum > 0;
  const flexibleInvalid = mode === "FLEXIBLE" && sumCaps > capNum + 0.01;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setSavedMsg(null);
    try {
      const { data } = await api.put("/wallet", {
        monthly_cap_eur: Number(cap),
        allocation_mode: mode,
        channel_caps: {
          google: Number(caps.google) || 0,
          meta: Number(caps.meta) || 0,
          tiktok: Number(caps.tiktok) || 0,
          linkedin: Number(caps.linkedin) || 0,
        },
      });
      setState(data);
      setSavedMsg(t("wallet.saved_ok"));
    } catch (err) {
      setError(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  if (!state) return <AppLayout><div className="py-24 text-center text-text-muted"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</div></AppLayout>;

  // M128 G4 — Gedeelde bron: `state.pacing` uit `wallet_pacing.compute_wallet_view`.
  // Sluit de UI aan op DEZELFDE data die /, /weekplan en /weekly-report zien.
  const pacing = state.pacing || {};
  const spentByChannel = pacing.spent_by_channel || {};
  const remainingByChannel = pacing.remaining_by_channel || {};
  const channelAllocations = pacing.channel_allocations || {};
  const totalSpentKnown = pacing.total_spent_known;
  const totalSpentEur = pacing.total_spent_eur;
  const totalRemainingEur = pacing.total_remaining_eur;
  const googleProjected = pacing.google_projected_month_eur;
  const googleProjectedPct = pacing.google_projected_pct_of_google_allocation;
  const hasAdsReport = !!pacing.last_updated_at;
  const CHANNEL_META = [
    { key: "google", label: "Google Ads" },
    { key: "meta", label: "Meta" },
    { key: "tiktok", label: "TikTok" },
    { key: "linkedin", label: "LinkedIn" },
  ];
  const fmtOrOnbekend = (v) =>
    v == null ? "onbekend — niet gekoppeld" : fmtEUR(v);
  const fmtOrOnbekendTotal = (v) =>
    v == null ? "onbekend" : fmtEUR(v);

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <>
          <div className="mb-6 flex items-center gap-3">
            <Wallet className="w-6 h-6 text-accent-teal" />
            <h1 className="text-2xl font-bold text-text-primary tracking-tight" data-testid="wallet-title">
              {t("wallet.title")}
            </h1>
          </div>
          <p className="text-sm text-text-muted mb-6">
            {t("wallet.subtitle")}
          </p>
        </>
      )}

      {/* Status — nu op gedeelde `pacing`-bron */}
      <div data-testid="wallet-status" className="grid sm:grid-cols-4 gap-3 mb-4">
        <StatCard label={t("wallet.stat_cap")}
                  value={state.monthly_cap_eur == null ? t("wallet.not_set_short") : fmtEUR(state.monthly_cap_eur)}
                  testid="wallet-stat-cap" />
        <StatCard label={t("wallet.stat_spent")}
                  value={fmtOrOnbekendTotal(totalSpentEur)}
                  testid="wallet-stat-spent" />
        <StatCard label={t("wallet.stat_remaining")}
                  value={fmtOrOnbekendTotal(totalRemainingEur)}
                  testid="wallet-stat-remaining" />
        <StatCard label={t("wallet.stat_mode")}
                  value={state.allocation_mode === "FIXED" ? t("wallet.mode_fixed") : state.allocation_mode === "FLEXIBLE" ? t("wallet.mode_flexible") : "—"}
                  testid="wallet-stat-mode" />
      </div>

      {/* Per-kanaal-breakdown uit gedeelde bron */}
      {state.monthly_cap_eur != null && (
        <div data-testid="wallet-channel-breakdown"
             className="mb-4 rounded-xl border border-border-hairline bg-bg-panel p-4">
          <div className="text-[11px] text-text-muted font-semibold mb-3">
            Uitgaven per kanaal deze maand
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {CHANNEL_META.map((ch) => (
              <div key={ch.key}
                   data-testid={`wallet-channel-breakdown-${ch.key}`}
                   className="rounded-md border border-border-hairline bg-bg-panel-raised p-3">
                <div className="text-[10px] text-text-muted font-semibold">
                  {ch.label}
                </div>
                <div className="text-lg text-text-primary tabular-nums mt-1"
                     data-testid={`wallet-channel-spent-${ch.key}`}>
                  {fmtOrOnbekend(spentByChannel[ch.key])}
                </div>
                <div className="text-[10px] text-text-muted mt-0.5"
                     data-testid={`wallet-channel-alloc-${ch.key}`}>
                  van plan {fmtEUR(channelAllocations[ch.key] || 0)}
                </div>
                {remainingByChannel[ch.key] != null && (
                  <div className="text-[10px] text-text-muted"
                       data-testid={`wallet-channel-remaining-${ch.key}`}>
                    resterend {fmtEUR(remainingByChannel[ch.key])}
                  </div>
                )}
              </div>
            ))}
          </div>
          {googleProjected != null && (
            <div className="mt-3 text-[11px] text-text-muted leading-relaxed"
                 data-testid="wallet-google-projection">
              Google-projectie deze maand: {fmtEUR(googleProjected)}
              {googleProjectedPct != null && (
                <> — {String(googleProjectedPct).replace(".", ",")}% van je Google-plan {fmtEUR(channelAllocations.google || 0)}.</>
              )}
            </div>
          )}
          <div className="mt-2 text-[10px] text-text-subtle"
               data-testid="wallet-shared-source-hint">
            {hasAdsReport
              ? `Google Ads: bijgewerkt op ${new Date(pacing.last_updated_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}. Andere kanalen: nog niet gekoppeld — bestedingen onbekend.`
              : "Nog geen bestedingsgegevens gekoppeld. Zodra u Google Ads koppelt, ziet u hier de echte maanduitgave."}
          </div>
        </div>
      )}

      {!hasAdsReport && !totalSpentKnown && (
        <div
          data-testid="wallet-planned-ceiling-note"
          className="mb-6 flex items-start gap-2 rounded-md border border-warn-amber/40 bg-warn-amber/10 p-3 text-xs text-warn-amber"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Nog geen bestedingsgegevens gekoppeld — dit is uw geplande plafond, geen gemeten uitgave.
            Zodra u Google Ads, Meta of TikTok koppelt, tonen we hier de echte maanduitgave.
          </span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={submit} className="rounded-xl border border-border-hairline bg-bg-panel p-6">
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="wallet-cap-input" className="text-xs text-text-muted font-semibold">
              {t("wallet.monthly_cap")}
            </label>
            <input
              id="wallet-cap-input"
              data-testid="wallet-cap-input"
              value={cap} onChange={(e) => setCap(e.target.value)}
              type="number" min="0" step="0.01"
              className="mt-1 w-full h-11 px-4 rounded-md bg-bg-panel border border-border-hairline focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/30"
            />
          </div>
          <div>
            <label className="text-xs text-text-muted font-semibold">
              {t("wallet.mode_label")}
            </label>
            <div className="mt-1 flex gap-2">
              {["FIXED", "FLEXIBLE"].map((m) => (
                <button type="button" key={m}
                  data-testid={`wallet-mode-${m.toLowerCase()}`}
                  onClick={() => setMode(m)}
                  className={`px-4 h-11 rounded-md text-sm font-semibold transition-colors border ${ mode === m ? "bg-accent-teal text-white border-blue-600" : "bg-bg-panel text-text-primary/85 border-border-hairline hover:border-accent-teal/60" }`}
                >
                  {m === "FIXED" ? t("wallet.mode_fixed") : t("wallet.mode_flexible")}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-text-muted italic">
              {mode === "FIXED" ? t("wallet.mode_fixed_hint") : t("wallet.mode_flexible_hint")}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {CHANNELS.map((ch) => (
            <div key={ch.key}>
              <label htmlFor={`wallet-cap-${ch.key}`} className="text-xs text-text-muted font-semibold">
                {ch.label} (€)
              </label>
              <input
                id={`wallet-cap-${ch.key}`}
                data-testid={`wallet-channel-${ch.key}-input`}
                value={caps[ch.key]}
                onChange={(e) => setCaps({ ...caps, [ch.key]: e.target.value })}
                type="number" min="0" step="0.01"
                className="mt-1 w-full h-11 px-4 rounded-md bg-bg-panel border border-border-hairline focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/30"
              />
            </div>
          ))}
        </div>

        <div data-testid="wallet-live-validation" className="mb-4 p-3 rounded-md bg-bg-panel border border-border-hairline text-sm flex items-start gap-2">
          {(fixedInvalid || flexibleInvalid || anyNegative) && <AlertCircle className="w-4 h-4 text-neg-red mt-0.5" />}
          <div>
            <div className="text-text-primary/85">
              Totaal van uw kanaalbudgetten: <span className="font-semibold tabular-nums">{fmtEUR(sumCaps)}</span>
              {" / "}
              <span className="font-semibold tabular-nums">{fmtEUR(capNum)}</span>
            </div>
            <div className="text-[11px] text-text-muted mt-0.5">
              JustEasySEO verdeelt uw maandbudget over kanalen zoals Google Ads, Meta en anderen. Deze verdeling moet optellen tot uw maandbudget.
            </div>
            {anyNegative && (
              <div data-testid="wallet-negative-channel-err" className="text-xs mt-0.5 text-neg-red">
                Bedragen per kanaal moeten 0 of hoger zijn.
              </div>
            )}
            {!anyNegative && mode === "FIXED" && capNum > 0 && (
              <div className={`text-xs mt-0.5 ${fixedInvalid ? "text-neg-red" : "text-pos-green"}`}>
                {fixedInvalid
                  ? `Optelsom moet gelijk zijn aan ${fmtEUR(capNum, 2)} (verschil: ${fmtEUR(sumCaps - capNum, 2)}).`
                  : "Optelsom klopt exact met uw maandbudget."}
              </div>
            )}
            {!anyNegative && mode === "FLEXIBLE" && capNum > 0 && (
              <div className={`text-xs mt-0.5 ${flexibleInvalid ? "text-neg-red" : "text-text-muted"}`}>
                {flexibleInvalid
                  ? `Overschrijding: ${fmtEUR(sumCaps - capNum, 2)}.`
                  : `Nog ${fmtEUR(remaining, 2)} beschikbaar binnen uw maandbudget.`}
              </div>
            )}
          </div>
        </div>

        {error && <div data-testid="wallet-error" className="mb-3 text-sm text-neg-red">{error}</div>}
        {savedMsg && <div data-testid="wallet-saved" className="mb-3 text-sm text-pos-green">{savedMsg}</div>}

        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="wallet-save-btn"
            disabled={saving || fixedInvalid || flexibleInvalid || anyNegative || !capNum}
            className="h-11 px-5 rounded-md bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-white font-semibold flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> {saving ? "…" : t("wallet.save")}
          </button>
        </div>
      </form>
    </AppLayout>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-border-hairline bg-bg-panel p-3">
      <div className="text-[10px] text-text-muted font-semibold">{label}</div>
      <div className="text-lg text-text-primary tabular-nums mt-1">{value}</div>
    </div>
  );
}
